import React, { useEffect, useState, useCallback } from 'react';
import CustomerSummaryReport from './CustomerSummaryReport';
import CdrReport from './CdrReport';
import axios from 'axios';
// 2026-10-24: The Executive Centre (tenant 2) bills in AED — new billing forms default to it
const BILL_DEF_CUR = (() => { try { return Number(JSON.parse(localStorage.getItem('ce_session') || '{}')?.user?.tenant_id) === 2 ? 'AED' : 'USD'; } catch (_) { return 'USD'; } })();

const API_BASE = '/api';

const api = {
  get:    (path: string, cfg?: any) => axios.get(API_BASE + path, cfg),
  post:   (path: string, data?: any) => axios.post(API_BASE + path, data),
  patch:  (path: string, data?: any) => axios.patch(API_BASE + path, data),
  delete: (path: string) => axios.delete(API_BASE + path),
};

const fmt  = (n: any, d = 4) => parseFloat(n || 0).toFixed(d);
const fmtM = (n: any)        => parseFloat(n || 0).toFixed(2);
const fmtS = (s: any) => { const t = parseInt(s || 0); return `${Math.floor(t/3600)}h ${Math.floor((t%3600)/60)}m ${t%60}s`; };

// ── Simple stat card ─────────────────────────────────────────────────────────
function StatCard({ label, value, sub, color = '#6366f1' }: any) {
  return (
    <div style={{ background: '#1e293b', borderRadius: 12, padding: '20px 24px', minWidth: 160, flex: 1, borderLeft: `4px solid ${color}` }}>
      <div style={{ color: '#94a3b8', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>{label}</div>
      <div style={{ color: '#f1f5f9', fontSize: 26, fontWeight: 700, margin: '6px 0 2px' }}>{value}</div>
      {sub && <div style={{ color: '#64748b', fontSize: 12 }}>{sub}</div>}
    </div>
  );
}

// ── Sub-pages ────────────────────────────────────────────────────────────────

function BillingDashboard() {
  const [data, setData] = useState<any>(null);
  useEffect(() => { api.get('/billing/dashboard').then((r:any)=>setData(r.data)).catch(()=>{}); }, []);
  if (!data) return <div style={{ color: '#94a3b8', padding: 40 }}>Loading...</div>;
  return (
    <div>
      <h2 style={{ color: '#f1f5f9', margin: '0 0 20px' }}>Billing Overview</h2>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
        <StatCard label="Total Balance"      value={`$${fmtM(data.balances?.total_balance)}`}  color="#22c55e" />
        <StatCard label="Month Spend"        value={`$${fmtM(data.monthSpend)}`}               color="#f59e0b" />
        <StatCard label="Today Spend"        value={`$${fmtM(data.todaySpend)}`}               color="#ef4444" />
        <StatCard label="Accounts"           value={data.balances?.accounts || 0}              color="#6366f1" />
        <StatCard label="Outstanding Inv."   value={data.outstandingInvoices || 0}             color="#f97316" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        <div style={{ background: '#1e293b', borderRadius: 12, padding: 20 }}>
          <h3 style={{ color: '#f1f5f9', margin: '0 0 12px', fontSize: 14 }}>Top Destinations (30 days)</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>{['Destination','Calls','Minutes','Revenue'].map(h=><th key={h} style={{ color:'#64748b',textAlign:'left',padding:'4px 8px',fontWeight:500 }}>{h}</th>)}</tr></thead>
            <tbody>{(data.topDestinations||[]).map((r:any,i:number)=>(
              <tr key={i} style={{ borderTop: '1px solid #334155' }}>
                <td style={{ color:'#e2e8f0',padding:'6px 8px' }}>{r.destination}</td>
                <td style={{ color:'#94a3b8',padding:'6px 8px' }}>{r.calls}</td>
                <td style={{ color:'#94a3b8',padding:'6px 8px' }}>{(parseFloat(r.dur||0)/60).toFixed(1)}</td>
                <td style={{ color:'#22c55e',padding:'6px 8px' }}>${fmtM(r.revenue)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        <div style={{ background: '#1e293b', borderRadius: 12, padding: 20 }}>
          <h3 style={{ color: '#f1f5f9', margin: '0 0 12px', fontSize: 14 }}>Daily Spend (30 days)</h3>
          {(data.dailySpend||[]).length === 0
            ? <div style={{ color:'#64748b',fontSize:13 }}>No call charges yet.</div>
            : <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12 }}>
                <thead><tr>{['Date','Calls','Revenue'].map(h=><th key={h} style={{ color:'#64748b',textAlign:'left',padding:'3px 6px',fontWeight:500 }}>{h}</th>)}</tr></thead>
                <tbody>{data.dailySpend.map((r:any,i:number)=>(
                  <tr key={i} style={{ borderTop:'1px solid #334155' }}>
                    <td style={{ color:'#e2e8f0',padding:'4px 6px' }}>{r.day}</td>
                    <td style={{ color:'#94a3b8',padding:'4px 6px' }}>{r.calls}</td>
                    <td style={{ color:'#22c55e',padding:'4px 6px' }}>${fmtM(r.revenue)}</td>
                  </tr>
                ))}</tbody>
              </table>}
        </div>
      </div>
    </div>
  );
}

function BillingAccounts({ user }: any) {
  const [accounts, setAccounts] = useState<any[]>([]);
  const [showNew, setShowNew]   = useState(false);
  const [form, setForm]         = useState<any>({ account_name:'', account_type:'prepaid', currency:BILL_DEF_CUR, credit_limit:0 });
  const [ratePlans, setRatePlans] = useState<any[]>([]);
  const [selected, setSelected]   = useState<any>(null);
  const [txns, setTxns]           = useState<any[]>([]);
  const [creditAmt, setCreditAmt] = useState('');
  const [debitAmt, setDebitAmt]   = useState('');

  const load = useCallback(() => {
    api.get('/billing/accounts').then((r:any)=>setAccounts(r.data.accounts||[])).catch(()=>{});
    api.get('/billing/rate-plans').then((r:any)=>setRatePlans(r.data.ratePlans||[])).catch(()=>{});
  }, []);
  useEffect(()=>{ load(); }, [load]);

  const loadTxns = (accId: number) => {
    api.get(`/billing/transactions?account_id=${accId}&limit=20`).then((r:any)=>setTxns(r.data.transactions||[])).catch(()=>{});
  };

  const createAccount = async () => {
    await api.post('/billing/accounts', form);
    setShowNew(false); setForm({ account_name:'', account_type:'prepaid', currency:BILL_DEF_CUR, credit_limit:0 }); load();
  };

  const credit = async (id: number) => {
    if (!creditAmt) return;
    await api.post(`/billing/accounts/${id}/credit`, { amount: parseFloat(creditAmt), reference: 'Manual credit' });
    setCreditAmt(''); load(); if (selected?.id === id) loadTxns(id);
  };

  const debit = async (id: number) => {
    if (!debitAmt) return;
    await api.post(`/billing/accounts/${id}/debit`, { amount: parseFloat(debitAmt), reference: 'Manual debit' });
    setDebitAmt(''); load(); if (selected?.id === id) loadTxns(id);
  };

  const suspend = async (id: number) => {
    await api.post(`/billing/accounts/${id}/suspend`, { reason: 'Manual suspension' }); load();
  };
  const restore = async (id: number) => {
    await api.post(`/billing/accounts/${id}/restore`, {}); load();
  };

  const statusBadge = (s: string) => ({
    active: '#22c55e', suspended: '#f59e0b', blocked: '#ef4444', closed: '#64748b'
  }[s] || '#94a3b8');

  const typeBadge = (t: string) => ({
    prepaid: '#6366f1', postpaid: '#f97316', wholesale: '#06b6d4', internal: '#64748b', test: '#94a3b8'
  }[t] || '#94a3b8');

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <h2 style={{ color:'#f1f5f9', margin:0 }}>Billing Accounts</h2>
        <button onClick={()=>setShowNew(true)} style={{ background:'#6366f1',color:'#fff',border:'none',borderRadius:8,padding:'8px 18px',cursor:'pointer',fontWeight:600 }}>+ New Account</button>
      </div>

      {showNew && (
        <div style={{ background:'#1e293b',borderRadius:12,padding:20,marginBottom:20 }}>
          <h3 style={{ color:'#f1f5f9',margin:'0 0 16px',fontSize:15 }}>New Billing Account</h3>
          <div style={{ display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12 }}>
            {[['account_name','Account Name','text'],['account_type','Type','select'],['currency','Currency','text'],['credit_limit','Credit Limit','number']].map(([k,l,t])=>(
              <div key={k}>
                <label style={{ color:'#94a3b8',fontSize:12,display:'block',marginBottom:4 }}>{l}</label>
                {t==='select'
                  ? <select value={form[k]} onChange={e=>setForm((f:any)=>({...f,[k]:e.target.value}))} style={{ width:'100%',background:'#0f172a',color:'#f1f5f9',border:'1px solid #334155',borderRadius:6,padding:'6px 10px' }}>
                      {['prepaid','postpaid','wholesale','reseller','internal','test'].map(o=><option key={o}>{o}</option>)}
                    </select>
                  : <input type={t} value={form[k]} onChange={e=>setForm((f:any)=>({...f,[k]:e.target.value}))} style={{ width:'100%',background:'#0f172a',color:'#f1f5f9',border:'1px solid #334155',borderRadius:6,padding:'6px 10px',boxSizing:'border-box' }} />}
              </div>
            ))}
            <div>
              <label style={{ color:'#94a3b8',fontSize:12,display:'block',marginBottom:4 }}>Rate Plan</label>
              <select value={form.rate_plan_id||''} onChange={e=>setForm((f:any)=>({...f,rate_plan_id:e.target.value}))} style={{ width:'100%',background:'#0f172a',color:'#f1f5f9',border:'1px solid #334155',borderRadius:6,padding:'6px 10px' }}>
                <option value=''>-- None --</option>
                {ratePlans.map((rp:any)=><option key={rp.id} value={rp.id}>{rp.name}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginTop:16, display:'flex', gap:10 }}>
            <button onClick={createAccount} style={{ background:'#22c55e',color:'#fff',border:'none',borderRadius:8,padding:'8px 18px',cursor:'pointer',fontWeight:600 }}>Create</button>
            <button onClick={()=>setShowNew(false)} style={{ background:'#334155',color:'#94a3b8',border:'none',borderRadius:8,padding:'8px 18px',cursor:'pointer' }}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:16 }}>
        {/* Account list */}
        <div>
          {accounts.map((acc:any)=>(
            <div key={acc.id} onClick={()=>{ setSelected(acc); loadTxns(acc.id); }}
              style={{ background: selected?.id===acc.id ? '#1e3a5f' : '#1e293b', borderRadius:10, padding:'14px 16px', marginBottom:10, cursor:'pointer', border: selected?.id===acc.id ? '1px solid #6366f1' : '1px solid transparent' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div>
                  <span style={{ color:'#f1f5f9', fontWeight:600, fontSize:14 }}>{acc.account_name}</span>
                  <span style={{ marginLeft:8, background:typeBadge(acc.account_type), color:'#fff', fontSize:10, borderRadius:4, padding:'2px 6px' }}>{acc.account_type}</span>
                  <span style={{ marginLeft:6, background: statusBadge(acc.status)+'22', color: statusBadge(acc.status), fontSize:10, borderRadius:4, padding:'2px 6px', border:`1px solid ${statusBadge(acc.status)}44` }}>{acc.status}</span>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ color:'#22c55e', fontWeight:700, fontSize:16 }}>${fmtM(acc.balance)}</div>
                  {parseFloat(acc.credit_limit)>0 && <div style={{ color:'#64748b',fontSize:11 }}>Limit: ${fmtM(acc.credit_limit)}</div>}
                </div>
              </div>
              {acc.rate_plan_name && <div style={{ color:'#64748b',fontSize:11,marginTop:4 }}>Plan: {acc.rate_plan_name}</div>}
            </div>
          ))}
          {accounts.length===0 && <div style={{ color:'#64748b',fontSize:13 }}>No accounts yet.</div>}
        </div>

        {/* Account detail */}
        {selected && (
          <div style={{ background:'#1e293b',borderRadius:12,padding:20 }}>
            <h3 style={{ color:'#f1f5f9',margin:'0 0 16px',fontSize:15 }}>{selected.account_name}</h3>
            <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:16 }}>
              <StatCard label="Balance" value={`$${fmtM(selected.balance)}`} color="#22c55e" />
              <StatCard label="Month Spend" value={`$${fmtM(selected.current_spend)}`} color="#f59e0b" />
            </div>
            <div style={{ display:'flex',gap:8,marginBottom:16 }}>
              <input placeholder="Amount" type="number" value={creditAmt} onChange={e=>setCreditAmt(e.target.value)} style={{ flex:1,background:'#0f172a',color:'#f1f5f9',border:'1px solid #334155',borderRadius:6,padding:'6px 10px' }} />
              <button onClick={()=>credit(selected.id)} style={{ background:'#22c55e',color:'#fff',border:'none',borderRadius:8,padding:'8px 14px',cursor:'pointer',fontWeight:600 }}>Credit</button>
              <button onClick={()=>debit(selected.id)} style={{ background:'#ef4444',color:'#fff',border:'none',borderRadius:8,padding:'8px 14px',cursor:'pointer',fontWeight:600 }}>Debit</button>
            </div>
            <div style={{ display:'flex',gap:8,marginBottom:16 }}>
              {selected.status==='active'
                ? <button onClick={()=>suspend(selected.id)} style={{ background:'#f59e0b',color:'#fff',border:'none',borderRadius:8,padding:'7px 14px',cursor:'pointer',fontSize:12 }}>Suspend</button>
                : <button onClick={()=>restore(selected.id)} style={{ background:'#22c55e',color:'#fff',border:'none',borderRadius:8,padding:'7px 14px',cursor:'pointer',fontSize:12 }}>Restore</button>}
            </div>
            <h4 style={{ color:'#94a3b8',fontSize:12,textTransform:'uppercase',margin:'16px 0 8px',letterSpacing:1 }}>Recent Transactions</h4>
            <div style={{ maxHeight:220,overflowY:'auto' }}>
              {txns.map((t:any)=>(
                <div key={t.id} style={{ display:'flex',justifyContent:'space-between',borderBottom:'1px solid #334155',padding:'6px 0',fontSize:12 }}>
                  <div>
                    <span style={{ color: t.transaction_type==='credit'||t.transaction_type==='payment' ? '#22c55e' : '#ef4444', fontWeight:600 }}>{t.transaction_type}</span>
                    <span style={{ color:'#64748b',marginLeft:8 }}>{t.reference||''}</span>
                  </div>
                  <div style={{ color:'#f1f5f9',fontWeight:600 }}>{t.transaction_type==='credit'||t.transaction_type==='payment'?'+':'-'}${fmt(t.amount)}</div>
                </div>
              ))}
              {txns.length===0 && <div style={{ color:'#64748b',fontSize:12 }}>No transactions.</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function RatePlansPage() {
  const [plans, setPlans]     = useState<any[]>([]);
  const [selected, setSelected] = useState<any>(null);
  const [rates, setRates]     = useState<any[]>([]);
  const [showNew, setShowNew] = useState(false);
  const [rateForm, setRateForm] = useState<any>({ prefix:'', description:'', country:'', cost_rate:'', sell_rate:'', increment:6 });
  const [planForm, setPlanForm] = useState<any>({ name:'', currency:BILL_DEF_CUR, billing_increment:6, minimum_duration:0 });
  const [search, setSearch]   = useState('');
  const [lookup, setLookup]   = useState<any>(null);
  const [lookupNum, setLookupNum] = useState('');
  const [canEdit, setCanEdit] = useState(false);       // TEC: Sub-Admins group + super admins only
  const [edit, setEdit] = useState<any>(null);         // rate row being edited
  // Excel export / bulk import (2026-10-28): import = preview first, then apply (versioned, never retroactive)
  const [imp, setImp] = useState<any>(null);           // { file, res, busy, msg }
  const FIELD: Record<string,string> = { cost_rate:'Cost', sell_rate:'Sell', increment:'Increment', minimum_duration:'Min duration', connect_fee:'Connect fee', description:'Destination', country:'Country', country_code:'Country code' };
  const exportXlsx = async () => {
    try {
      const r:any = await api.get(`/billing/rate-plans/${selected.id}/export.xlsx`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data); const a = document.createElement('a');
      a.href = url; a.download = `rates-${String(selected.name).replace(/[^A-Za-z0-9]+/g,'-')}.xlsx`; document.body.appendChild(a); a.click(); a.remove(); setTimeout(()=>URL.revokeObjectURL(url), 2000);
    } catch (e) { fail(e); }
  };
  const runImport = async (apply: boolean) => {
    if (!imp?.file) { setImp({ ...imp, msg:'Choose the Excel file first' }); return; }
    if (apply && !window.confirm(`Apply ${imp.res.adds} new, ${imp.res.updates} changed and ${imp.res.deletes} removed rate(s) to "${selected.name}"?\nNew calls use the new prices from now on; calls already made are not changed.`)) return;
    setImp({ ...imp, busy:true, msg:'' });
    try {
      const fd = new FormData(); fd.append('file', imp.file);
      const r:any = await api.post(`/billing/rate-plans/${selected.id}/import?apply=${apply ? 1 : 0}`, fd);
      if (apply) { setImp({ file:null, res:null, busy:false, msg:`Saved: ${r.data.adds} added, ${r.data.updates} changed, ${r.data.deletes} removed.`, done:true }); loadRates(selected.id); loadPlans(); }
      else setImp({ ...imp, res:r.data, busy:false, msg:'' });
    } catch (e:any) { setImp({ ...imp, busy:false, msg: e?.response?.data?.error || e?.message || 'Import failed' }); }
  };
  useEffect(()=>{ api.get('/billing/rate-plan-access').then((r:any)=>setCanEdit(!!r.data?.edit)).catch(()=>setCanEdit(false)); }, []);
  const money = (v:any) => { const n = parseFloat(v); const s = isNaN(n) ? '—' : n.toFixed(4); return !selected || selected.currency === 'USD' ? `$${s}` : `${s} ${selected.currency}`; };
  const fail = (e:any) => alert(e?.response?.data?.error || e?.message || 'Could not save');
  const saveEdit = async () => {
    const num = (x:any) => x === '' || x === null || isNaN(Number(x)) ? null : Number(x);
    if (!String(edit.prefix||'').trim() || num(edit.sell_rate) === null || num(edit.cost_rate) === null || !(Number(edit.increment) > 0)) { alert('Prefix, cost, sell and increment are required (numbers).'); return; }
    if (!window.confirm(`Save ${edit.prefix} — ${edit.description || ''}?\nNew calls are charged with the new price from now on; calls already made are not changed.`)) return;
    try {
      await api.patch(`/billing/rate-plans/${selected.id}/rates/${edit.id}`, { prefix: String(edit.prefix).trim(), description: edit.description, country: edit.country,
        cost_rate: num(edit.cost_rate), sell_rate: num(edit.sell_rate), increment: Number(edit.increment) });
      setEdit(null); loadRates(selected.id);
    } catch (e) { fail(e); }
  };

  const loadPlans = () => api.get('/billing/rate-plans').then((r:any)=>setPlans(r.data.ratePlans||[])).catch(()=>{});
  const loadRates = (planId: number) => api.get(`/billing/rate-plans/${planId}/rates?search=${search}`).then((r:any)=>setRates(r.data.rates||[])).catch(()=>{});
  useEffect(()=>{ loadPlans(); }, []);
  useEffect(()=>{ if(selected) loadRates(selected.id); }, [selected, search]);

  const addRate = async () => {
    try { await api.post(`/billing/rate-plans/${selected.id}/rates`, rateForm); } catch (e) { fail(e); return; }
    setRateForm({ prefix:'', description:'', country:'', cost_rate:'', sell_rate:'', increment:6 });
    loadRates(selected.id);
  };
  const deleteRate = async (rateId: number) => {
    if (!window.confirm('Delete this rate? Calls to this destination will use the next matching prefix.')) return;
    try { await api.delete(`/billing/rate-plans/${selected.id}/rates/${rateId}`); } catch (e) { fail(e); return; }
    loadRates(selected.id);
  };
  const createPlan = async () => {
    try { await api.post('/billing/rate-plans', planForm); } catch (e) { fail(e); return; }
    setPlanForm({ name:'', currency:BILL_DEF_CUR, billing_increment:6, minimum_duration:0 });
    setShowNew(false); loadPlans();
  };
  const doLookup = async () => {
    if (!selected || !lookupNum) return;
    const r:any = await api.get(`/billing/rate-lookup?rate_plan_id=${selected.id}&destination=${lookupNum}`);
    setLookup(r.data);
  };

  return (
    <div>
      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20 }}>
        <h2 style={{ color:'#f1f5f9',margin:0 }}>Rate Plans</h2>
        {canEdit ? <button onClick={()=>setShowNew(true)} style={{ background:'#6366f1',color:'#fff',border:'none',borderRadius:8,padding:'8px 18px',cursor:'pointer',fontWeight:600 }}>+ New Plan</button>
          : <span style={{ color:'#94a3b8',fontSize:12 }}>View only — price lists are changed by the Sub-Admins group</span>}
      </div>

      {showNew && (
        <div style={{ background:'#1e293b',borderRadius:12,padding:20,marginBottom:20 }}>
          <h3 style={{ color:'#f1f5f9',margin:'0 0 12px',fontSize:15 }}>New Rate Plan</h3>
          <div style={{ display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12 }}>
            {[['name','Plan Name'],['currency','Currency'],['billing_increment','Increment (sec)'],['minimum_duration','Min Duration (sec)']].map(([k,l])=>(
              <div key={k}>
                <label style={{ color:'#94a3b8',fontSize:12,display:'block',marginBottom:4 }}>{l}</label>
                <input value={planForm[k]} onChange={e=>setPlanForm((f:any)=>({...f,[k]:e.target.value}))} style={{ width:'100%',background:'#0f172a',color:'#f1f5f9',border:'1px solid #334155',borderRadius:6,padding:'6px 10px',boxSizing:'border-box' }} />
              </div>
            ))}
          </div>
          <div style={{ marginTop:12,display:'flex',gap:8 }}>
            <button onClick={createPlan} style={{ background:'#22c55e',color:'#fff',border:'none',borderRadius:8,padding:'8px 16px',cursor:'pointer',fontWeight:600 }}>Create</button>
            <button onClick={()=>setShowNew(false)} style={{ background:'#334155',color:'#94a3b8',border:'none',borderRadius:8,padding:'8px 16px',cursor:'pointer' }}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ display:'grid',gridTemplateColumns:'260px 1fr',gap:20 }}>
        <div>
          {plans.map((p:any)=>(
            <div key={p.id} onClick={()=>setSelected(p)}
              style={{ background:selected?.id===p.id?'#1e3a5f':'#1e293b',borderRadius:10,padding:'12px 14px',marginBottom:8,cursor:'pointer',border:selected?.id===p.id?'1px solid #6366f1':'1px solid transparent' }}>
              <div style={{ color:'#f1f5f9',fontWeight:600,fontSize:13 }}>{p.name} {p.is_default&&<span style={{ color:'#6366f1',fontSize:10 }}>DEFAULT</span>}</div>
              <div style={{ color:'#64748b',fontSize:11,marginTop:2 }}>{p.rate_count} destinations · {p.currency} · {p.billing_increment}s inc</div>
            </div>
          ))}
        </div>

        {selected && (
          <div>
            <div style={{ background:'#1e293b',borderRadius:12,padding:16,marginBottom:16 }}>
              <div style={{ display:'flex',gap:8,alignItems:'center',marginBottom:12 }}>
                <input placeholder="Search prefix/description..." value={search} onChange={e=>setSearch(e.target.value)} style={{ flex:1,background:'#0f172a',color:'#f1f5f9',border:'1px solid #334155',borderRadius:6,padding:'6px 10px' }} />
                <input placeholder="Test number..." value={lookupNum} onChange={e=>setLookupNum(e.target.value)} style={{ width:160,background:'#0f172a',color:'#f1f5f9',border:'1px solid #334155',borderRadius:6,padding:'6px 10px' }} />
                <button onClick={doLookup} style={{ background:'#06b6d4',color:'#fff',border:'none',borderRadius:8,padding:'8px 14px',cursor:'pointer',fontWeight:600 }}>Lookup</button>
                <button onClick={exportXlsx} title="Download this price list as Excel" style={{ background:'#334155',color:'#f1f5f9',border:'none',borderRadius:8,padding:'8px 14px',cursor:'pointer',fontWeight:600,whiteSpace:'nowrap' }}>Export Excel</button>
                {canEdit && <button onClick={()=>setImp({ file:null, res:null, busy:false, msg:'' })} title="Update many rates at once from an Excel file" style={{ background:'#6366f1',color:'#fff',border:'none',borderRadius:8,padding:'8px 14px',cursor:'pointer',fontWeight:600,whiteSpace:'nowrap' }}>Import Excel</button>}
              </div>
              {imp && canEdit && (
                <div style={{ background:'#0f172a',border:'1px solid #334155',borderRadius:8,padding:12,marginBottom:12,fontSize:12,color:'#cbd5e1' }}>
                  <div style={{ display:'flex',gap:8,alignItems:'center',flexWrap:'wrap' }}>
                    <b style={{ color:'#f1f5f9' }}>Bulk update from Excel</b>
                    <input type="file" accept=".xlsx" onChange={e=>setImp({ file:(e.target.files && e.target.files[0]) || null, res:null, busy:false, msg:'' })} style={{ color:'#cbd5e1' }} />
                    <button disabled={!imp.file || imp.busy} onClick={()=>runImport(false)} style={{ background:'#06b6d4',color:'#fff',border:'none',borderRadius:6,padding:'6px 12px',cursor:'pointer',fontWeight:600,opacity:(!imp.file||imp.busy)?0.5:1 }}>{imp.busy && !imp.res ? 'Checking…' : 'Preview'}</button>
                    {imp.res && <button disabled={imp.busy || imp.res.errors > 0 || !(imp.res.adds + imp.res.updates + imp.res.deletes)} onClick={()=>runImport(true)} style={{ background:'#22c55e',color:'#fff',border:'none',borderRadius:6,padding:'6px 12px',cursor:'pointer',fontWeight:600,opacity:(imp.busy||imp.res.errors>0||!(imp.res.adds+imp.res.updates+imp.res.deletes))?0.5:1 }}>Apply {imp.res.adds + imp.res.updates + imp.res.deletes} change(s)</button>}
                    <button onClick={()=>setImp(null)} style={{ background:'#334155',color:'#94a3b8',border:'none',borderRadius:6,padding:'6px 12px',cursor:'pointer' }}>Close</button>
                  </div>
                  <div style={{ marginTop:6,color:'#94a3b8' }}>Tip: Export first, edit prices in Excel, add rows for new prefixes, type DELETE in the Action column to remove one. Rows left out are kept. Prices apply to new calls only.</div>
                  {imp.msg && <div style={{ marginTop:8,color: imp.done ? '#22c55e' : '#f87171',fontWeight:600 }}>{imp.msg}</div>}
                  {imp.res && (
                    <div style={{ marginTop:10 }}>
                      <div>{imp.res.rows} row(s) read · <span style={{ color:'#22c55e' }}>{imp.res.adds} new</span> · <span style={{ color:'#f59e0b' }}>{imp.res.updates} changed</span> · <span style={{ color:'#f87171' }}>{imp.res.deletes} to remove</span> · {imp.res.unchanged} unchanged{imp.res.errors ? <> · <b style={{ color:'#f87171' }}>{imp.res.errors} error(s) — fix the file and preview again</b></> : null}</div>
                      <div style={{ maxHeight:260,overflow:'auto',marginTop:8 }}>
                        {imp.res.preview.errors.map((x:any,i:number)=><div key={'e'+i} style={{ color:'#f87171' }}>Row {x.row}: {x.error}</div>)}
                        {imp.res.preview.updates.map((x:any)=><div key={'u'+x.prefix}><span style={{ color:'#f59e0b' }}>Change</span> {x.prefix} {x.description || ''} — {x.changes.map((c:any)=>`${FIELD[c.field]||c.field}: ${c.before} → ${c.after}`).join(', ')}</div>)}
                        {imp.res.preview.adds.map((x:any)=><div key={'a'+x.prefix}><span style={{ color:'#22c55e' }}>New</span> {x.prefix} {x.description || ''} — sell {x.sell_rate}, cost {x.cost_rate}, {x.increment}s</div>)}
                        {imp.res.preview.deletes.map((x:any)=><div key={'d'+x.prefix}><span style={{ color:'#f87171' }}>Remove</span> {x.prefix} {x.description || ''}</div>)}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {lookup && (
                <div style={{ background:'#0f172a',borderRadius:8,padding:10,marginBottom:12,fontSize:12 }}>
                  {lookup.rate ? (
                    <><span style={{ color:'#22c55e' }}>✓ Matched: </span><span style={{ color:'#f1f5f9' }}>{lookup.rate.description} (prefix: {lookup.rate.prefix})</span><span style={{ color:'#f59e0b',marginLeft:12 }}>Cost: {money(lookup.rate.cost_rate)}/min · Sell: {money(lookup.rate.sell_rate)}/min</span></>
                  ) : <span style={{ color:'#ef4444' }}>✗ No rate found for this destination</span>}
                </div>
              )}
              <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12 }}>
                <thead>
                  <tr>{['Prefix','Description','Country','Cost/min','Sell/min','Inc(s)',''].map(h=><th key={h} style={{ color:'#64748b',textAlign:'left',padding:'6px 8px',fontWeight:500,borderBottom:'1px solid #334155' }}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {canEdit && <tr>
                    {['prefix','description','country','cost_rate','sell_rate','increment'].map(k=>(
                      <td key={k} style={{ padding:'4px 4px' }}>
                        <input placeholder={k} value={rateForm[k]} onChange={e=>setRateForm((f:any)=>({...f,[k]:e.target.value}))}
                          style={{ width:'100%',background:'#0f172a',color:'#22c55e',border:'1px solid #334155',borderRadius:4,padding:'4px 6px',boxSizing:'border-box' }} />
                      </td>
                    ))}
                    <td style={{ padding:'4px 4px' }}><button onClick={addRate} style={{ background:'#22c55e',color:'#fff',border:'none',borderRadius:6,padding:'5px 12px',cursor:'pointer',fontWeight:600,whiteSpace:'nowrap' }}>Add</button></td>
                  </tr>}
                  {rates.map((r:any)=> edit && edit.id === r.id ? (
                    <tr key={r.id} style={{ borderTop:'1px solid #1e293b',background:'#0f172a' }}>
                      {['prefix','description','country','cost_rate','sell_rate','increment'].map(k=>(
                        <td key={k} style={{ padding:'4px 4px' }}>
                          <input value={edit[k] ?? ''} onChange={e=>setEdit((x:any)=>({...x,[k]:e.target.value}))} onKeyDown={e=>{ if (e.key==='Enter') saveEdit(); if (e.key==='Escape') setEdit(null); }}
                            style={{ width:'100%',background:'#1e293b',color:'#f1f5f9',border:'1px solid #6366f1',borderRadius:4,padding:'4px 6px',boxSizing:'border-box' }} />
                        </td>))}
                      <td style={{ padding:'4px 4px',whiteSpace:'nowrap' }}>
                        <button onClick={saveEdit} style={{ background:'#22c55e',color:'#fff',border:'none',borderRadius:4,padding:'3px 10px',cursor:'pointer',fontSize:11,fontWeight:600,marginRight:4 }}>Save</button>
                        <button onClick={()=>setEdit(null)} style={{ background:'#334155',color:'#cbd5e1',border:'none',borderRadius:4,padding:'3px 8px',cursor:'pointer',fontSize:11 }}>Cancel</button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={r.id} style={{ borderTop:'1px solid #1e293b' }}>
                      <td style={{ color:'#f1f5f9',padding:'5px 8px',fontFamily:'monospace' }}>{r.prefix}</td>
                      <td style={{ color:'#94a3b8',padding:'5px 8px' }}>{r.description}</td>
                      <td style={{ color:'#94a3b8',padding:'5px 8px' }}>{r.country}</td>
                      <td style={{ color:'#f59e0b',padding:'5px 8px' }}>{money(r.cost_rate)}</td>
                      <td style={{ color:'#22c55e',padding:'5px 8px' }}>{money(r.sell_rate)}</td>
                      <td style={{ color:'#94a3b8',padding:'5px 8px' }}>{r.increment}</td>
                      <td style={{ padding:'5px 8px',whiteSpace:'nowrap' }}>{canEdit && <>
                        <button onClick={()=>setEdit({ ...r })} style={{ background:'#6366f122',color:'#a5b4fc',border:'1px solid #6366f144',borderRadius:4,padding:'2px 8px',cursor:'pointer',fontSize:11,marginRight:4 }}>Edit</button>
                        <button onClick={()=>deleteRate(r.id)} style={{ background:'#ef444422',color:'#ef4444',border:'1px solid #ef444444',borderRadius:4,padding:'2px 8px',cursor:'pointer',fontSize:11 }}>Del</button></>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InvoicesPage() {
  const [invoices, setInvoices] = useState<any[]>([]);
  const [brk, setBrk] = useState<any>(null);   // invoice id whose per-location breakdown is open
  const [accounts, setAccounts] = useState<any[]>([]);
  const [genForm, setGenForm]   = useState<any>({ account_id:'', period_start:'', period_end:'' });
  const [msg, setMsg]           = useState('');

  useEffect(()=>{
    api.get('/billing/invoices').then((r:any)=>setInvoices(r.data.invoices||[])).catch(()=>{});
    api.get('/billing/accounts').then((r:any)=>setAccounts(r.data.accounts||[])).catch(()=>{});
  }, []);

  const generate = async () => {
    try {
      const r:any = await api.post('/billing/invoices/generate', genForm);
      setMsg(`Invoice ${r.data.invoiceNumber} generated. Total due: $${parseFloat(r.data.totalDue).toFixed(4)}`);
      api.get('/billing/invoices').then((r2:any)=>setInvoices(r2.data.invoices||[])).catch(()=>{});
    } catch(e:any) { setMsg(`Error: ${e.response?.data?.error||e.message}`); }
  };

  const downloadPDF = async (id: number) => {
    const r:any = await api.get(`/billing/invoices/${id}/pdf`, { responseType:'blob' });
    const url = URL.createObjectURL(new Blob([r.data],{type:'application/pdf'}));
    const a = document.createElement('a'); a.href=url; a.download=`invoice-${id}.pdf`; a.click();
  };

  const statusColor:any = { draft:'#94a3b8', sent:'#6366f1', paid:'#22c55e', overdue:'#ef4444', void:'#64748b' };

  return (
    <div>
      <h2 style={{ color:'#f1f5f9',margin:'0 0 20px' }}>Invoices</h2>
      <div style={{ background:'#1e293b',borderRadius:12,padding:20,marginBottom:24 }}>
        <h3 style={{ color:'#f1f5f9',margin:'0 0 14px',fontSize:15 }}>Generate Invoice</h3>
        {msg && <div style={{ background:'#22c55e22',border:'1px solid #22c55e44',borderRadius:8,padding:'8px 12px',marginBottom:12,color:'#22c55e',fontSize:13 }}>{msg}</div>}
        <div style={{ display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12 }}>
          <div>
            <label style={{ color:'#94a3b8',fontSize:12,display:'block',marginBottom:4 }}>Account</label>
            <select value={genForm.account_id} onChange={e=>setGenForm((f:any)=>({...f,account_id:e.target.value}))} style={{ width:'100%',background:'#0f172a',color:'#f1f5f9',border:'1px solid #334155',borderRadius:6,padding:'6px 10px' }}>
              <option value=''>-- Select Account --</option>
              {accounts.map((a:any)=><option key={a.id} value={a.id}>{a.account_name}</option>)}
            </select>
          </div>
          {[['period_start','Period Start'],['period_end','Period End']].map(([k,l])=>(
            <div key={k}>
              <label style={{ color:'#94a3b8',fontSize:12,display:'block',marginBottom:4 }}>{l}</label>
              <input type="datetime-local" value={genForm[k]} onChange={e=>setGenForm((f:any)=>({...f,[k]:e.target.value}))} style={{ width:'100%',background:'#0f172a',color:'#f1f5f9',border:'1px solid #334155',borderRadius:6,padding:'6px 10px',boxSizing:'border-box' }} />
            </div>
          ))}
        </div>
        <button onClick={generate} style={{ marginTop:14,background:'#6366f1',color:'#fff',border:'none',borderRadius:8,padding:'8px 20px',cursor:'pointer',fontWeight:600 }}>Generate Invoice</button>
      </div>

      <div style={{ background:'#1e293b',borderRadius:12,padding:20 }}>
        <h3 style={{ color:'#f1f5f9',margin:'0 0 12px',fontSize:15 }}>All Invoices</h3>
        <table style={{ width:'100%',borderCollapse:'collapse',fontSize:13 }}>
          <thead><tr>{['Invoice #','Account','Period','Subtotal','Tax','Total Due','Status',''].map(h=><th key={h} style={{ color:'#64748b',textAlign:'left',padding:'6px 8px',fontWeight:500,borderBottom:'1px solid #334155' }}>{h}</th>)}</tr></thead>
          <tbody>
            {invoices.map((inv:any)=>(<React.Fragment key={inv.id}>
              <tr key={inv.id} style={{ borderTop:'1px solid #1e2a3a' }}>
                <td style={{ color:'#6366f1',padding:'8px',fontWeight:600 }}>{inv.invoice_number}</td>
                <td style={{ color:'#e2e8f0',padding:'8px' }}>{inv.account_name}</td>
                <td style={{ color:'#94a3b8',padding:'8px',fontSize:11 }}>{new Date(inv.period_start).toLocaleDateString()} – {new Date(inv.period_end).toLocaleDateString()}</td>
                <td style={{ color:'#f1f5f9',padding:'8px' }}>${fmtM(inv.subtotal)}</td>
                <td style={{ color:'#f59e0b',padding:'8px' }}>${fmtM(inv.tax_amount)}</td>
                <td style={{ color:'#22c55e',padding:'8px',fontWeight:700 }}>${fmtM(inv.total_due)}</td>
                <td style={{ padding:'8px' }}><span style={{ background:statusColor[inv.status]+'22',color:statusColor[inv.status],border:`1px solid ${statusColor[inv.status]}44`,borderRadius:4,padding:'2px 8px',fontSize:11 }}>{inv.status}</span></td>
                <td style={{ padding:'8px' }}>{Array.isArray(inv.location_breakdown) && inv.location_breakdown.length > 0 && <button onClick={()=>setBrk(brk===inv.id?null:inv.id)} style={{ background:'#334155',color:'#94a3b8',border:'none',borderRadius:6,padding:'4px 10px',cursor:'pointer',fontSize:11,marginRight:6 }}>Locations</button>}<button onClick={()=>downloadPDF(inv.id)} style={{ background:'#334155',color:'#94a3b8',border:'none',borderRadius:6,padding:'4px 10px',cursor:'pointer',fontSize:11 }}>PDF</button></td>
              </tr>
              {brk===inv.id && <tr><td colSpan={8} style={{ padding:'4px 8px 12px' }}>
                <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12,background:'#0f172a',borderRadius:8 }}>
                  <thead><tr>{['Location','Site','Calls','Minutes','Call charges','Number fees'].map(h=><th key={h} style={{ color:'#64748b',textAlign:h==='Location'||h==='Site'?'left':'right',padding:'6px 8px',fontWeight:500 }}>{h}</th>)}</tr></thead>
                  <tbody>{inv.location_breakdown.map((r:any,k:number)=>(<tr key={k} style={{ borderTop:'1px solid #1e2a3a' }}>
                    <td style={{ color:r.location_id?'#e2e8f0':'#f59e0b',padding:'6px 8px' }}>{r.location}</td><td style={{ color:'#94a3b8' }}>{r.site||'—'}</td>
                    <td style={{ color:'#e2e8f0',textAlign:'right' }}>{r.calls}</td><td style={{ color:'#e2e8f0',textAlign:'right' }}>{r.minutes}</td>
                    <td style={{ color:'#f1f5f9',textAlign:'right' }}>{fmtM(r.call_amount)}</td><td style={{ color:'#94a3b8',textAlign:'right',paddingRight:8 }}>{r.number_fees?`${fmtM(r.number_amount)} (${r.number_fees})`:'—'}</td></tr>))}</tbody>
                </table></td></tr>}
            </React.Fragment>))}
          </tbody>
        </table>
        {invoices.length===0 && <div style={{ color:'#64748b',fontSize:13,marginTop:12 }}>No invoices yet. Generate one above.</div>}
      </div>
    </div>
  );
}

function BillingReports({ siteId = '' }: any) {
  const [tab, setTab]   = useState<'user'|'dest'|'summary'|'cdr'>('dest');
  const [sumOn, setSumOn] = useState(false);   // customer summary — organisations with customer accounts (TEC)
  useEffect(() => { axios.get('/api/customers/config').then((r: any) => { if (r.data && r.data.enabled) { setSumOn(true); setTab('summary'); } }).catch(() => {}); }, []);
  const [rows, setRows] = useState<any[]>([]);
  const [from, setFrom] = useState('');
  const [to, setTo]     = useState('');

  const load = () => {
    const q = `from=${from}&to=${to}`;
    const url = tab==='dest' ? `/billing/reports/by-destination?${q}` : `/billing/reports/by-user?${q}`;
    api.get(url).then((r:any)=>setRows(r.data.rows||[])).catch(()=>{});
  };
  useEffect(()=>{ if (tab === 'dest' || tab === 'user') load(); }, [tab]);

  return (
    <div>
      <h2 style={{ color:'#f1f5f9',margin:'0 0 20px' }}>Billing Reports</h2>
      <div style={{ display:'flex',gap:8,alignItems:'center',marginBottom:20 }}>
        {((sumOn ? ['summary','cdr','dest','user'] : ['cdr','dest','user']) as ('summary'|'dest'|'user'|'cdr')[]).map(t=>(
          <button key={t} onClick={()=>setTab(t)} style={{ background:tab===t?'#6366f1':'#1e293b',color:tab===t?'#fff':'#94a3b8',border:'none',borderRadius:8,padding:'8px 16px',cursor:'pointer',fontWeight:600 }}>
            {t==='summary'?'Customer Summary':t==='cdr'?'CDR Report':t==='dest'?'By Destination':'By User'}
          </button>
        ))}
        {(tab === 'dest' || tab === 'user') && <>
        <input type="date" value={from} onChange={e=>setFrom(e.target.value)} style={{ background:'#1e293b',color:'#f1f5f9',border:'1px solid #334155',borderRadius:6,padding:'6px 10px' }} />
        <span style={{ color:'#64748b' }}>→</span>
        <input type="date" value={to} onChange={e=>setTo(e.target.value)} style={{ background:'#1e293b',color:'#f1f5f9',border:'1px solid #334155',borderRadius:6,padding:'6px 10px' }} />
        <button onClick={load} style={{ background:'#6366f1',color:'#fff',border:'none',borderRadius:8,padding:'8px 16px',cursor:'pointer',fontWeight:600 }}>Run</button>
        </>}
      </div>
      {tab === 'cdr' ? <CdrReport siteId={siteId} /> : tab === 'summary' ? <CustomerSummaryReport siteId={siteId} /> :
      <div style={{ background:'#1e293b',borderRadius:12,padding:20 }}>
        <table style={{ width:'100%',borderCollapse:'collapse',fontSize:13 }}>
          <thead>
            {tab==='dest'
              ? <tr>{['Destination','Country','Calls','Duration','Revenue','Cost','Margin'].map(h=><th key={h} style={{ color:'#64748b',textAlign:'left',padding:'6px 8px',fontWeight:500,borderBottom:'1px solid #334155' }}>{h}</th>)}</tr>
              : <tr>{['User','Calls','Duration','Revenue','Cost','Margin'].map(h=><th key={h} style={{ color:'#64748b',textAlign:'left',padding:'6px 8px',fontWeight:500,borderBottom:'1px solid #334155' }}>{h}</th>)}</tr>}
          </thead>
          <tbody>
            {rows.map((r:any,i:number)=>(
              <tr key={i} style={{ borderTop:'1px solid #1e2a3a' }}>
                {tab==='dest'
                  ? <><td style={{ color:'#f1f5f9',padding:'7px 8px',fontFamily:'monospace' }}>{r.destination}</td><td style={{ color:'#94a3b8',padding:'7px 8px' }}>{r.country||'—'}</td></>
                  : <td style={{ color:'#f1f5f9',padding:'7px 8px' }}>{r.display_name||r.username||'Unknown'}</td>}
                <td style={{ color:'#94a3b8',padding:'7px 8px' }}>{r.calls}</td>
                <td style={{ color:'#94a3b8',padding:'7px 8px' }}>{fmtS(r.total_sec)}</td>
                <td style={{ color:'#22c55e',padding:'7px 8px',fontWeight:600 }}>${fmtM(r.revenue)}</td>
                <td style={{ color:'#f59e0b',padding:'7px 8px' }}>${fmtM(r.cost)}</td>
                <td style={{ color:'#6366f1',padding:'7px 8px' }}>${fmtM(r.margin)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length===0 && <div style={{ color:'#64748b',fontSize:13,marginTop:12 }}>No data for the selected period.</div>}
      </div>}
    </div>
  );
}

// ── Main BillingPage container ───────────────────────────────────────────────
// Shared input style for all billing subpages
const inputStyle: React.CSSProperties = {
  width: '100%', background: '#0f172a', color: '#f1f5f9',
  border: '1px solid #334155', borderRadius: 6, padding: '6px 10px',
  boxSizing: 'border-box' as const, fontSize: 13,
};

// ── Carriers & LCR ──────────────────────────────────────────────────────────
function CarriersPage() {
  const [carriers, setCarriers] = React.useState<any[]>([]);
  const [form, setForm] = React.useState({ name:'', host:'', port:5060, transport:'UDP', username:'', password:'', priority:10, weight:100, max_channels:0, notes:'' });
  const [lcrDest, setLcrDest] = React.useState('');
  const [lcrResult, setLcrResult] = React.useState<any>(null);
  const token = localStorage.getItem('token');
  const h = { headers: { Authorization: 'Bearer ' + token } };
  const load = () => axios.get(API_BASE + '/billing/carriers', h).then(r => setCarriers(r.data.carriers || []));
  React.useEffect(() => { load(); }, []);
  const create = async () => {
    await axios.post(API_BASE + '/billing/carriers', form, h);
    setForm({ name:'', host:'', port:5060, transport:'UDP', username:'', password:'', priority:10, weight:100, max_channels:0, notes:'' });
    load();
  };
  const del = async (id: number) => { if (window.confirm('Delete carrier?')) { await axios.delete(API_BASE + '/billing/carriers/' + id, h); load(); } };
  const lcrLookup = async () => {
    const r = await axios.post(API_BASE + '/billing/lcr/lookup', { destination: lcrDest }, h);
    setLcrResult(r.data.result);
  };
  const statusColor: any = { active:'#22c55e', inactive:'#94a3b8', maintenance:'#f59e0b' };
  return (
    <div>
      <h2 style={{ color:'#f1f5f9',margin:'0 0 20px' }}>Carriers & LCR</h2>
      <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:20,marginBottom:24 }}>
        <div style={{ background:'#1e293b',borderRadius:12,padding:20 }}>
          <h3 style={{ color:'#f1f5f9',margin:'0 0 14px',fontSize:14 }}>Add Carrier</h3>
          {[['name','Name'],['host','SIP Host'],['username','Username'],['password','Password'],['notes','Notes']].map(([k,l])=>(
            <div key={k} style={{ marginBottom:8 }}>
              <div style={{ fontSize:11,color:'#94a3b8',marginBottom:3 }}>{l}</div>
              <input value={(form as any)[k]} onChange={e=>setForm({...form,[k]:e.target.value})} style={inputStyle} />
            </div>
          ))}
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,marginBottom:8 }}>
            {[['port','Port'],['priority','Priority'],['weight','Weight']].map(([k,l])=>(
              <div key={k}>
                <div style={{ fontSize:11,color:'#94a3b8',marginBottom:3 }}>{l}</div>
                <input type="number" value={(form as any)[k]} onChange={e=>setForm({...form,[k]:parseInt(e.target.value)})} style={inputStyle} />
              </div>
            ))}
          </div>
          <button onClick={create} style={{ background:'#6366f1',color:'#fff',border:'none',borderRadius:8,padding:'8px 20px',cursor:'pointer',fontWeight:600,width:'100%' }}>Add Carrier</button>
        </div>
        <div style={{ background:'#1e293b',borderRadius:12,padding:20 }}>
          <h3 style={{ color:'#f1f5f9',margin:'0 0 14px',fontSize:14 }}>LCR Test</h3>
          <div style={{ fontSize:11,color:'#94a3b8',marginBottom:6 }}>Destination (E.164)</div>
          <div style={{ display:'flex',gap:8,marginBottom:12 }}>
            <input value={lcrDest} onChange={e=>setLcrDest(e.target.value)} placeholder="+971501234567" style={{...inputStyle,flex:1}} />
            <button onClick={lcrLookup} style={{ background:'#0ea5e9',color:'#fff',border:'none',borderRadius:8,padding:'8px 14px',cursor:'pointer',fontWeight:600 }}>Lookup</button>
          </div>
          {lcrResult && (
            <div style={{ background:'#0f172a',borderRadius:8,padding:12,fontSize:12 }}>
              <div style={{ color:'#22c55e',fontWeight:700,marginBottom:6 }}>Best Route Found</div>
              <div style={{ color:'#94a3b8' }}>Carrier: <span style={{ color:'#f1f5f9' }}>{lcrResult.carrier_name}</span></div>
              <div style={{ color:'#94a3b8' }}>Cost Rate: <span style={{ color:'#f1f5f9' }}>${parseFloat(lcrResult.cost_rate).toFixed(6)}/min</span></div>
              <div style={{ color:'#94a3b8' }}>Priority: <span style={{ color:'#f1f5f9' }}>{lcrResult.priority}</span></div>
            </div>
          )}
        </div>
      </div>
      <div style={{ background:'#1e293b',borderRadius:12,padding:20 }}>
        <h3 style={{ color:'#f1f5f9',margin:'0 0 12px',fontSize:14 }}>Active Carriers ({carriers.length})</h3>
        <table style={{ width:'100%',borderCollapse:'collapse',fontSize:13 }}>
          <thead><tr>{['Name','Host','Priority','Weight','Channels','Success%','Status',''].map(h=><th key={h} style={{ color:'#64748b',fontSize:11,textAlign:'left',padding:'6px 10px',fontWeight:600 }}>{h}</th>)}</tr></thead>
          <tbody>{carriers.map(c=>(
            <tr key={c.id} style={{ borderTop:'1px solid #334155' }}>
              <td style={{ color:'#f1f5f9',padding:'8px 10px',fontWeight:600 }}>{c.name}</td>
              <td style={{ color:'#94a3b8',padding:'8px 10px' }}>{c.host}:{c.port}</td>
              <td style={{ color:'#94a3b8',padding:'8px 10px' }}>{c.priority}</td>
              <td style={{ color:'#94a3b8',padding:'8px 10px' }}>{c.weight}</td>
              <td style={{ color:'#94a3b8',padding:'8px 10px' }}>{c.current_channels}/{c.max_channels||'∞'}</td>
              <td style={{ color:'#94a3b8',padding:'8px 10px' }}>{c.success_rate}%</td>
              <td style={{ padding:'8px 10px' }}><span style={{ background:(statusColor[c.status]||'#94a3b8')+'22',color:statusColor[c.status]||'#94a3b8',borderRadius:4,padding:'2px 8px',fontSize:11 }}>{c.status}</span></td>
              <td style={{ padding:'8px 10px' }}><button onClick={()=>del(c.id)} style={{ background:'#ef444422',color:'#ef4444',border:'none',borderRadius:6,padding:'4px 10px',cursor:'pointer',fontSize:12 }}>Delete</button></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

// ── Packages & Bundles ───────────────────────────────────────────────────────
function BundlesPage() {
  const [bundles, setBundles] = React.useState<any[]>([]);
  const [subs, setSubs] = React.useState<any[]>([]);
  const [form, setForm] = React.useState({ name:'', price:'0', currency:BILL_DEF_CUR, included_minutes:0, period:'monthly', prefix_filter:'', rollover_enabled:false, overage_rate:'0', description:'' });
  const [subForm, setSubForm] = React.useState({ account_id:'', bundle_id:'' });
  const [accounts, setAccounts] = React.useState<any[]>([]);
  const token = localStorage.getItem('token');
  const h = { headers: { Authorization: 'Bearer ' + token } };
  const load = () => {
    axios.get(API_BASE + '/billing/bundles', h).then(r => setBundles(r.data.bundles || []));
    axios.get(API_BASE + '/billing/bundle-subscriptions', h).then(r => setSubs(r.data.subscriptions || []));
    axios.get(API_BASE + '/billing/accounts', h).then(r => setAccounts(r.data.accounts || []));
  };
  React.useEffect(() => { load(); }, []);
  const create = async () => { await axios.post(API_BASE + '/billing/bundles', form, h); setForm({ name:'', price:'0', currency:BILL_DEF_CUR, included_minutes:0, period:'monthly', prefix_filter:'', rollover_enabled:false, overage_rate:'0', description:'' }); load(); };
  const subscribe = async () => { await axios.post(API_BASE + '/billing/bundle-subscriptions', subForm, h); setSubForm({ account_id:'', bundle_id:'' }); load(); };
  return (
    <div>
      <h2 style={{ color:'#f1f5f9',margin:'0 0 20px' }}>Packages & Bundles</h2>
      <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:20,marginBottom:20 }}>
        <div style={{ background:'#1e293b',borderRadius:12,padding:20 }}>
          <h3 style={{ color:'#f1f5f9',margin:'0 0 14px',fontSize:14 }}>Create Bundle</h3>
          {[['name','Name'],['description','Description'],['price','Price'],['overage_rate','Overage Rate/min']].map(([k,l])=>(
            <div key={k} style={{ marginBottom:8 }}>
              <div style={{ fontSize:11,color:'#94a3b8',marginBottom:3 }}>{l}</div>
              <input value={(form as any)[k]} onChange={e=>setForm({...form,[k]:e.target.value})} style={inputStyle} />
            </div>
          ))}
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8 }}>
            <div>
              <div style={{ fontSize:11,color:'#94a3b8',marginBottom:3 }}>Included Minutes</div>
              <input type="number" value={form.included_minutes} onChange={e=>setForm({...form,included_minutes:parseInt(e.target.value)})} style={inputStyle} />
            </div>
            <div>
              <div style={{ fontSize:11,color:'#94a3b8',marginBottom:3 }}>Period</div>
              <select value={form.period} onChange={e=>setForm({...form,period:e.target.value})} style={inputStyle}>
                <option>monthly</option><option>yearly</option><option>weekly</option>
              </select>
            </div>
          </div>
          <div style={{ marginBottom:8 }}>
            <div style={{ fontSize:11,color:'#94a3b8',marginBottom:3 }}>Prefix Filter (optional)</div>
            <input value={form.prefix_filter} onChange={e=>setForm({...form,prefix_filter:e.target.value})} placeholder="971 = UAE only" style={inputStyle} />
          </div>
          <label style={{ display:'flex',alignItems:'center',gap:8,color:'#94a3b8',fontSize:13,marginBottom:10,cursor:'pointer' }}>
            <input type="checkbox" checked={form.rollover_enabled} onChange={e=>setForm({...form,rollover_enabled:e.target.checked})} />
            Enable minute rollover
          </label>
          <button onClick={create} style={{ background:'#6366f1',color:'#fff',border:'none',borderRadius:8,padding:'8px 20px',cursor:'pointer',fontWeight:600,width:'100%' }}>Create Bundle</button>
        </div>
        <div style={{ background:'#1e293b',borderRadius:12,padding:20 }}>
          <h3 style={{ color:'#f1f5f9',margin:'0 0 14px',fontSize:14 }}>Subscribe Account</h3>
          <div style={{ marginBottom:10 }}>
            <div style={{ fontSize:11,color:'#94a3b8',marginBottom:4 }}>Account</div>
            <select value={subForm.account_id} onChange={e=>setSubForm({...subForm,account_id:e.target.value})} style={inputStyle}>
              <option value="">Select account...</option>
              {accounts.map((a:any)=><option key={a.id} value={a.id}>{a.account_name}</option>)}
            </select>
          </div>
          <div style={{ marginBottom:10 }}>
            <div style={{ fontSize:11,color:'#94a3b8',marginBottom:4 }}>Bundle</div>
            <select value={subForm.bundle_id} onChange={e=>setSubForm({...subForm,bundle_id:e.target.value})} style={inputStyle}>
              <option value="">Select bundle...</option>
              {bundles.map((b:any)=><option key={b.id} value={b.id}>{b.name} — {b.included_minutes}min/{b.period}</option>)}
            </select>
          </div>
          <button onClick={subscribe} style={{ background:'#22c55e',color:'#fff',border:'none',borderRadius:8,padding:'8px 20px',cursor:'pointer',fontWeight:600,width:'100%' }}>Subscribe</button>
          <div style={{ marginTop:20 }}>
            <h4 style={{ color:'#94a3b8',fontSize:12,textTransform:'uppercase',margin:'0 0 10px' }}>Active Subscriptions</h4>
            {subs.map((s:any)=>(
              <div key={s.id} style={{ background:'#0f172a',borderRadius:8,padding:10,marginBottom:8,fontSize:12 }}>
                <div style={{ color:'#f1f5f9',fontWeight:600 }}>{s.bundle_name}</div>
                <div style={{ color:'#94a3b8' }}>Account #{s.account_id} · {parseFloat(s.minutes_used||0).toFixed(1)}/{s.included_minutes} min used</div>
                <div style={{ color:'#64748b',fontSize:11 }}>Renews: {s.period_end ? new Date(s.period_end).toLocaleDateString() : 'never'}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div style={{ background:'#1e293b',borderRadius:12,padding:20 }}>
        <h3 style={{ color:'#f1f5f9',margin:'0 0 12px',fontSize:14 }}>All Bundles</h3>
        <table style={{ width:'100%',borderCollapse:'collapse',fontSize:13 }}>
          <thead><tr>{['Name','Price','Minutes','Period','Prefix','Rollover','Overage',''].map(h=><th key={h} style={{ color:'#64748b',fontSize:11,textAlign:'left',padding:'6px 8px' }}>{h}</th>)}</tr></thead>
          <tbody>{bundles.map((b:any)=>(
            <tr key={b.id} style={{ borderTop:'1px solid #334155' }}>
              <td style={{ color:'#f1f5f9',padding:'8px',fontWeight:600 }}>{b.name}</td>
              <td style={{ color:'#22c55e',padding:'8px' }}>${parseFloat(b.price).toFixed(2)}</td>
              <td style={{ color:'#94a3b8',padding:'8px' }}>{b.included_minutes}</td>
              <td style={{ color:'#94a3b8',padding:'8px' }}>{b.period}</td>
              <td style={{ color:'#94a3b8',padding:'8px' }}>{b.prefix_filter||'—'}</td>
              <td style={{ color:'#94a3b8',padding:'8px' }}>{b.rollover_enabled?'✓':'—'}</td>
              <td style={{ color:'#94a3b8',padding:'8px' }}>${parseFloat(b.overage_rate||0).toFixed(4)}/min</td>
              <td style={{ padding:'8px' }}><span style={{ background:b.is_active?'#22c55e22':'#ef444422',color:b.is_active?'#22c55e':'#ef4444',borderRadius:4,padding:'2px 8px',fontSize:11 }}>{b.is_active?'Active':'Inactive'}</span></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

// ── DID Billing ──────────────────────────────────────────────────────────────
function DidBillingPage() {
  const [dids, setDids] = React.useState<any[]>([]);
  const [editing, setEditing] = React.useState<any>(null);
  const [accounts, setAccounts] = React.useState<any[]>([]);
  const [editForm, setEditForm] = React.useState({ billing_account_id:'', monthly_rate:'0', billing_day:1, currency:BILL_DEF_CUR });
  const [runResult, setRunResult] = React.useState<any>(null);
  const token = localStorage.getItem('token');
  const h = { headers: { Authorization: 'Bearer ' + token } };
  const load = () => {
    axios.get(API_BASE + '/billing/dids', h).then(r => setDids(r.data.dids || []));
    axios.get(API_BASE + '/billing/accounts', h).then(r => setAccounts(r.data.accounts || []));
  };
  React.useEffect(() => { load(); }, []);
  const save = async () => {
    await axios.patch(API_BASE + '/billing/dids/' + editing.id, editForm, h);
    setEditing(null); load();
  };
  const runBilling = async () => {
    const r = await axios.post(API_BASE + '/billing/dids/run-billing', {}, h);
    setRunResult(r.data);
  };
  return (
    <div>
      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20 }}>
        <h2 style={{ color:'#f1f5f9',margin:0 }}>DID Billing</h2>
        <div style={{ display:'flex',gap:10,alignItems:'center' }}>
          {runResult && <span style={{ color:'#22c55e',fontSize:13 }}>Charged: {runResult.charged} | Failed: {runResult.failed}</span>}
          <button onClick={runBilling} style={{ background:'#0ea5e9',color:'#fff',border:'none',borderRadius:8,padding:'8px 16px',cursor:'pointer',fontWeight:600 }}>Run DID Billing Now</button>
        </div>
      </div>
      {editing && (
        <div style={{ background:'#1e293b',borderRadius:12,padding:20,marginBottom:20 }}>
          <h3 style={{ color:'#f1f5f9',margin:'0 0 14px',fontSize:14 }}>Set Billing for {editing.did_number}</h3>
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:10,marginBottom:12 }}>
            <div>
              <div style={{ fontSize:11,color:'#94a3b8',marginBottom:4 }}>Billing Account</div>
              <select value={editForm.billing_account_id} onChange={e=>setEditForm({...editForm,billing_account_id:e.target.value})} style={inputStyle}>
                <option value="">None</option>
                {accounts.map((a:any)=><option key={a.id} value={a.id}>{a.account_name}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize:11,color:'#94a3b8',marginBottom:4 }}>Monthly Rate ($)</div>
              <input value={editForm.monthly_rate} onChange={e=>setEditForm({...editForm,monthly_rate:e.target.value})} style={inputStyle} />
            </div>
            <div>
              <div style={{ fontSize:11,color:'#94a3b8',marginBottom:4 }}>Billing Day</div>
              <input type="number" min={1} max={28} value={editForm.billing_day} onChange={e=>setEditForm({...editForm,billing_day:parseInt(e.target.value)})} style={inputStyle} />
            </div>
            <div>
              <div style={{ fontSize:11,color:'#94a3b8',marginBottom:4 }}>Currency</div>
              <input value={editForm.currency} onChange={e=>setEditForm({...editForm,currency:e.target.value})} style={inputStyle} />
            </div>
          </div>
          <div style={{ display:'flex',gap:8 }}>
            <button onClick={save} style={{ background:'#22c55e',color:'#fff',border:'none',borderRadius:8,padding:'8px 20px',cursor:'pointer',fontWeight:600 }}>Save</button>
            <button onClick={()=>setEditing(null)} style={{ background:'#334155',color:'#94a3b8',border:'none',borderRadius:8,padding:'8px 20px',cursor:'pointer' }}>Cancel</button>
          </div>
        </div>
      )}
      <div style={{ background:'#1e293b',borderRadius:12,padding:20 }}>
        <table style={{ width:'100%',borderCollapse:'collapse',fontSize:13 }}>
          <thead><tr>{['DID Number','Account','Monthly Rate','Billing Day','Last Billed',''].map(hd=><th key={hd} style={{ color:'#64748b',fontSize:11,textAlign:'left',padding:'6px 8px' }}>{hd}</th>)}</tr></thead>
          <tbody>{dids.map((d:any)=>(
            <tr key={d.id} style={{ borderTop:'1px solid #334155' }}>
              <td style={{ color:'#f1f5f9',padding:'8px',fontWeight:600,fontFamily:'monospace' }}>{d.did_number}</td>
              <td style={{ color:'#94a3b8',padding:'8px' }}>{d.account_name||'—'}</td>
              <td style={{ color:d.monthly_rate>0?'#22c55e':'#64748b',padding:'8px' }}>${parseFloat(d.monthly_rate||0).toFixed(2)}</td>
              <td style={{ color:'#94a3b8',padding:'8px' }}>{d.billing_day||1}</td>
              <td style={{ color:'#64748b',padding:'8px',fontSize:11 }}>{d.last_billed_at?new Date(d.last_billed_at).toLocaleDateString():'Never'}</td>
              <td style={{ padding:'8px' }}>
                <button onClick={()=>{ setEditing(d); setEditForm({ billing_account_id:d.billing_account_id||'', monthly_rate:d.monthly_rate||'0', billing_day:d.billing_day||1, currency:d.currency||'USD' }); }} style={{ background:'#6366f122',color:'#818cf8',border:'none',borderRadius:6,padding:'4px 10px',cursor:'pointer',fontSize:12 }}>Edit</button>
              </td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

// ── Reseller Billing ─────────────────────────────────────────────────────────
function ResellerPage() {
  const [accounts, setAccounts] = React.useState<any[]>([]);
  const [selected, setSelected] = React.useState<any>(null);
  const [markups, setMarkups] = React.useState<any[]>([]);
  const [subAccounts, setSubAccounts] = React.useState<any[]>([]);
  const [markupForm, setMarkupForm] = React.useState({ markup_type:'percentage', markup_value:'10', prefix_filter:'', description:'' });
  const [subForm, setSubForm] = React.useState({ account_name:'', billing_email:'', currency:BILL_DEF_CUR });
  const [report, setReport] = React.useState<any[]>([]);
  const token = localStorage.getItem('token');
  const h = { headers: { Authorization: 'Bearer ' + token } };
  React.useEffect(() => { axios.get(API_BASE + '/billing/accounts', h).then(r => setAccounts((r.data.accounts||[]).filter((a:any)=>a.account_type==='reseller'))); }, []);
  const selectAccount = async (acc: any) => {
    setSelected(acc);
    const [m, s, r] = await Promise.all([
      axios.get(API_BASE + '/billing/reseller/' + acc.id + '/markups', h),
      axios.get(API_BASE + '/billing/reseller/' + acc.id + '/sub-accounts', h),
      axios.get(API_BASE + '/billing/reseller/' + acc.id + '/margin-report', h),
    ]);
    setMarkups(m.data.markups || []);
    setSubAccounts(s.data.subAccounts || []);
    setReport(r.data.report || []);
  };
  const addMarkup = async () => { await axios.post(API_BASE + '/billing/reseller/' + selected.id + '/markups', markupForm, h); selectAccount(selected); };
  const addSub = async () => { await axios.post(API_BASE + '/billing/reseller/' + selected.id + '/sub-accounts', subForm, h); selectAccount(selected); };
  return (
    <div>
      <h2 style={{ color:'#f1f5f9',margin:'0 0 20px' }}>Reseller Billing</h2>
      <div style={{ display:'grid',gridTemplateColumns:'280px 1fr',gap:20 }}>
        <div>
          <div style={{ background:'#1e293b',borderRadius:12,padding:16 }}>
            <div style={{ color:'#94a3b8',fontSize:11,fontWeight:600,textTransform:'uppercase',marginBottom:10 }}>Reseller Accounts</div>
            {accounts.length===0 && <div style={{ color:'#64748b',fontSize:12 }}>No reseller accounts. Create a billing account with type "reseller".</div>}
            {accounts.map((a:any)=>(
              <div key={a.id} onClick={()=>selectAccount(a)} style={{ padding:'10px 12px',borderRadius:8,cursor:'pointer',background:selected?.id===a.id?'#1e3a5f':'transparent',marginBottom:4 }}>
                <div style={{ color:'#f1f5f9',fontWeight:600,fontSize:13 }}>{a.account_name}</div>
                <div style={{ color:'#64748b',fontSize:11 }}>Balance: ${parseFloat(a.balance||0).toFixed(2)}</div>
              </div>
            ))}
          </div>
        </div>
        {selected ? (
          <div>
            <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16 }}>
              <div style={{ background:'#1e293b',borderRadius:12,padding:16 }}>
                <h4 style={{ color:'#f1f5f9',margin:'0 0 12px',fontSize:13 }}>Markup Rules</h4>
                {[['markup_type','Type'],['markup_value','Value'],['prefix_filter','Prefix Filter'],['description','Description']].map(([k,l])=>(
                  <div key={k} style={{ marginBottom:6 }}>
                    <div style={{ fontSize:11,color:'#94a3b8',marginBottom:2 }}>{l}</div>
                    {k==='markup_type' ? (
                      <select value={markupForm.markup_type} onChange={e=>setMarkupForm({...markupForm,markup_type:e.target.value})} style={inputStyle}>
                        <option value="percentage">Percentage %</option>
                        <option value="flat_per_minute">Flat per minute</option>
                        <option value="fixed">Fixed rate</option>
                      </select>
                    ) : <input value={(markupForm as any)[k]} onChange={e=>setMarkupForm({...markupForm,[k]:e.target.value})} style={inputStyle} />}
                  </div>
                ))}
                <button onClick={addMarkup} style={{ background:'#6366f1',color:'#fff',border:'none',borderRadius:8,padding:'7px 16px',cursor:'pointer',fontWeight:600,marginTop:6,width:'100%' }}>Add Rule</button>
                <div style={{ marginTop:12 }}>{markups.map((m:any)=>(
                  <div key={m.id} style={{ background:'#0f172a',borderRadius:6,padding:'8px 10px',marginBottom:4,fontSize:11,color:'#94a3b8' }}>
                    {m.markup_type}: <span style={{ color:'#f1f5f9' }}>{m.markup_value}</span>{m.prefix_filter?' | '+m.prefix_filter:''}
                  </div>
                ))}</div>
              </div>
              <div style={{ background:'#1e293b',borderRadius:12,padding:16 }}>
                <h4 style={{ color:'#f1f5f9',margin:'0 0 12px',fontSize:13 }}>Add Sub-Account</h4>
                {[['account_name','Name'],['billing_email','Billing Email'],['currency','Currency']].map(([k,l])=>(
                  <div key={k} style={{ marginBottom:6 }}>
                    <div style={{ fontSize:11,color:'#94a3b8',marginBottom:2 }}>{l}</div>
                    <input value={(subForm as any)[k]} onChange={e=>setSubForm({...subForm,[k]:e.target.value})} style={inputStyle} />
                  </div>
                ))}
                <button onClick={addSub} style={{ background:'#22c55e',color:'#fff',border:'none',borderRadius:8,padding:'7px 16px',cursor:'pointer',fontWeight:600,marginTop:6,width:'100%' }}>Create Sub-Account</button>
                <div style={{ marginTop:12 }}>
                  {subAccounts.map((s:any)=>(
                    <div key={s.id} style={{ background:'#0f172a',borderRadius:6,padding:'8px 10px',marginBottom:4,fontSize:11 }}>
                      <span style={{ color:'#f1f5f9' }}>{s.account_name}</span>
                      <span style={{ color:'#64748b',marginLeft:8 }}>Balance: ${parseFloat(s.balance||0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ background:'#1e293b',borderRadius:12,padding:16 }}>
              <h4 style={{ color:'#f1f5f9',margin:'0 0 12px',fontSize:13 }}>Margin Report</h4>
              <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12 }}>
                <thead><tr>{['Destination','Calls','Revenue','Cost','Margin','Margin%'].map(hd=><th key={hd} style={{ color:'#64748b',fontSize:11,textAlign:'left',padding:'5px 8px' }}>{hd}</th>)}</tr></thead>
                <tbody>{report.map((r:any,i:number)=>(
                  <tr key={i} style={{ borderTop:'1px solid #334155' }}>
                    <td style={{ color:'#f1f5f9',padding:'6px 8px',fontFamily:'monospace' }}>{r.destination}</td>
                    <td style={{ color:'#94a3b8',padding:'6px 8px' }}>{r.calls}</td>
                    <td style={{ color:'#22c55e',padding:'6px 8px' }}>${parseFloat(r.customer_revenue||0).toFixed(4)}</td>
                    <td style={{ color:'#f97316',padding:'6px 8px' }}>${parseFloat(r.carrier_cost||0).toFixed(4)}</td>
                    <td style={{ color:'#6366f1',padding:'6px 8px' }}>${parseFloat(r.margin||0).toFixed(4)}</td>
                    <td style={{ color:'#94a3b8',padding:'6px 8px' }}>{r.margin_pct}%</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        ) : <div style={{ background:'#1e293b',borderRadius:12,padding:40,textAlign:'center',color:'#64748b' }}>Select a reseller account to manage</div>}
      </div>
    </div>
  );
}

// ── Revenue Assurance ────────────────────────────────────────────────────────
function RevenueAssurancePage() {
  const [alerts, setAlerts] = React.useState<any[]>([]);
  const [summary, setSummary] = React.useState<any>(null);
  const [running, setRunning] = React.useState(false);
  const token = localStorage.getItem('token');
  const h = { headers: { Authorization: 'Bearer ' + token } };
  const load = () => {
    axios.get(API_BASE + '/billing/ra/alerts', h).then(r => setAlerts(r.data.alerts || []));
    axios.get(API_BASE + '/billing/ra/daily-summary', h).then(r => setSummary(r.data.summary)).catch(()=>{});
  };
  React.useEffect(() => { load(); }, []);
  const runChecks = async () => { setRunning(true); await axios.post(API_BASE + '/billing/ra/run-checks', {}, h); setRunning(false); load(); };
  const resolve = async (id: number) => { await axios.patch(API_BASE + '/billing/ra/alerts/' + id + '/resolve', {}, h); load(); };
  const sevColor: any = { critical:'#ef4444', warning:'#f59e0b', info:'#6366f1' };
  return (
    <div>
      <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20 }}>
        <h2 style={{ color:'#f1f5f9',margin:0 }}>Revenue Assurance</h2>
        <button onClick={runChecks} disabled={running} style={{ background:'#6366f1',color:'#fff',border:'none',borderRadius:8,padding:'8px 18px',cursor:'pointer',fontWeight:600 }}>{running?'Running...':'Run Checks Now'}</button>
      </div>
      {summary && (
        <div style={{ display:'flex',gap:14,marginBottom:20,flexWrap:'wrap' }}>
          {[
            { label:'Today Revenue', value:'$'+parseFloat(summary.total_revenue||0).toFixed(2), color:'#22c55e' },
            { label:'Today Cost',    value:'$'+parseFloat(summary.total_cost||0).toFixed(2), color:'#f97316' },
            { label:'Today Margin',  value:'$'+parseFloat(summary.total_margin||0).toFixed(2), color:'#6366f1' },
            { label:'Margin %',      value:(summary.margin_pct||0)+'%', color:'#0ea5e9' },
            { label:'Calls Today',   value:summary.total_calls||0, color:'#f1f5f9' },
          ].map((m:any)=>(
            <div key={m.label} style={{ background:'#1e293b',borderRadius:10,padding:'14px 20px',flex:'1',minWidth:120 }}>
              <div style={{ color:'#64748b',fontSize:11,marginBottom:4 }}>{m.label}</div>
              <div style={{ color:m.color,fontSize:22,fontWeight:700 }}>{m.value}</div>
            </div>
          ))}
        </div>
      )}
      <div style={{ background:'#1e293b',borderRadius:12,padding:20 }}>
        <h3 style={{ color:'#f1f5f9',margin:'0 0 14px',fontSize:14 }}>Active Alerts ({alerts.length})</h3>
        {alerts.length===0 && <div style={{ color:'#64748b',fontSize:13 }}>No active alerts. System looks healthy.</div>}
        {alerts.map((a:any)=>(
          <div key={a.id} style={{ background:'#0f172a',borderRadius:8,padding:'12px 16px',marginBottom:8,borderLeft:'3px solid '+(sevColor[a.severity]||'#94a3b8'),display:'flex',justifyContent:'space-between',alignItems:'flex-start' }}>
            <div>
              <div style={{ display:'flex',gap:8,alignItems:'center',marginBottom:4 }}>
                <span style={{ background:(sevColor[a.severity]||'#94a3b8')+'22',color:sevColor[a.severity]||'#94a3b8',borderRadius:4,padding:'2px 8px',fontSize:11,fontWeight:600 }}>{a.severity.toUpperCase()}</span>
                <span style={{ color:'#64748b',fontSize:11,fontFamily:'monospace' }}>{a.alert_type}</span>
              </div>
              <div style={{ color:'#f1f5f9',fontSize:13 }}>{a.description}</div>
              <div style={{ color:'#64748b',fontSize:11,marginTop:4 }}>{new Date(a.created_at).toLocaleString()}</div>
            </div>
            <button onClick={()=>resolve(a.id)} style={{ background:'#22c55e22',color:'#22c55e',border:'none',borderRadius:6,padding:'5px 12px',cursor:'pointer',fontSize:12,fontWeight:600,whiteSpace:'nowrap' }}>Resolve</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Tax Settings ─────────────────────────────────────────────────────────────
function TaxSettingsPage() {
  const [taxes, setTaxes] = React.useState<any[]>([]);
  const [form, setForm] = React.useState({ tax_name:'VAT', tax_rate:'5', tax_number:'', applies_to:'all' });
  const token = localStorage.getItem('token');
  const h = { headers: { Authorization: 'Bearer ' + token } };
  const load = () => axios.get(API_BASE + '/billing/tax-configs', h).then(r => setTaxes(r.data.taxConfigs || []));
  React.useEffect(() => { load(); }, []);
  const create = async () => { await axios.post(API_BASE + '/billing/tax-configs', form, h); setForm({ tax_name:'VAT', tax_rate:'5', tax_number:'', applies_to:'all' }); load(); };
  const toggle = async (t: any) => { await axios.patch(API_BASE + '/billing/tax-configs/' + t.id, { is_active: !t.is_active }, h); load(); };
  return (
    <div>
      <h2 style={{ color:'#f1f5f9',margin:'0 0 20px' }}>Tax Settings</h2>
      <div style={{ display:'grid',gridTemplateColumns:'360px 1fr',gap:20 }}>
        <div style={{ background:'#1e293b',borderRadius:12,padding:20 }}>
          <h3 style={{ color:'#f1f5f9',margin:'0 0 14px',fontSize:14 }}>Add Tax</h3>
          {[['tax_name','Tax Name'],['tax_rate','Rate (%)'],['tax_number','Tax Registration Number']].map(([k,l])=>(
            <div key={k} style={{ marginBottom:8 }}>
              <div style={{ fontSize:11,color:'#94a3b8',marginBottom:3 }}>{l}</div>
              <input value={(form as any)[k]} onChange={e=>setForm({...form,[k]:e.target.value})} style={inputStyle} />
            </div>
          ))}
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:11,color:'#94a3b8',marginBottom:3 }}>Applies To</div>
            <select value={form.applies_to} onChange={e=>setForm({...form,applies_to:e.target.value})} style={inputStyle}>
              <option value="all">All Charges</option>
              <option value="calls">Calls Only</option>
              <option value="dids">DIDs Only</option>
              <option value="bundles">Bundles Only</option>
            </select>
          </div>
          <button onClick={create} style={{ background:'#6366f1',color:'#fff',border:'none',borderRadius:8,padding:'8px 20px',cursor:'pointer',fontWeight:600,width:'100%' }}>Add Tax</button>
          <div style={{ marginTop:12,color:'#64748b',fontSize:11 }}>Tax rates are automatically applied to all new invoices. UAE VAT is 5%.</div>
        </div>
        <div style={{ background:'#1e293b',borderRadius:12,padding:20 }}>
          <h3 style={{ color:'#f1f5f9',margin:'0 0 14px',fontSize:14 }}>Configured Tax Rules</h3>
          {taxes.map((t:any)=>(
            <div key={t.id} style={{ background:'#0f172a',borderRadius:8,padding:'12px 16px',marginBottom:8,display:'flex',justifyContent:'space-between',alignItems:'center' }}>
              <div>
                <div style={{ color:'#f1f5f9',fontWeight:600 }}>{t.tax_name} — {t.tax_rate}%</div>
                {t.tax_number && <div style={{ color:'#64748b',fontSize:11 }}>Reg: {t.tax_number}</div>}
                <div style={{ color:'#64748b',fontSize:11 }}>Applies to: {t.applies_to}</div>
              </div>
              <button onClick={()=>toggle(t)} style={{ background:t.is_active?'#22c55e22':'#ef444422',color:t.is_active?'#22c55e':'#ef4444',border:'none',borderRadius:6,padding:'5px 12px',cursor:'pointer',fontSize:12,fontWeight:600 }}>{t.is_active?'Active':'Disabled'}</button>
            </div>
          ))}
          {taxes.length===0 && <div style={{ color:'#64748b',fontSize:13 }}>No tax rules configured.</div>}
        </div>
      </div>
    </div>
  );
}

export default function BillingPage({ user, tab: initTab, siteId = '' }: any) {
  type TabKey = 'dashboard'|'accounts'|'rate-plans'|'invoices'|'reports'|'carriers'|'bundles'|'dids'|'reseller'|'ra'|'tax';
  const tabMap: Record<string, TabKey> = {
    'Billing Dashboard': 'dashboard', 'Billing Accounts': 'accounts',
    'Rate Plans': 'rate-plans', 'Invoices': 'invoices', 'Billing Reports': 'reports',
    'Carriers & LCR': 'carriers', 'Packages & Bundles': 'bundles',
    'DID Billing': 'dids', 'Reseller Billing': 'reseller',
    'Revenue Assurance': 'ra', 'Tax Settings': 'tax',
  };
  const [tab, setTab] = useState<TabKey>(tabMap[initTab] || 'dashboard');
  React.useEffect(() => { if (tabMap[initTab]) setTab(tabMap[initTab]); }, [initTab]);
  const tabs: { key: TabKey; label: string }[] = ([
    { key:'dashboard',  label:'Dashboard'   },
    { key:'accounts',   label:'Accounts'    },
    { key:'rate-plans', label:'Rate Plans'  },
    { key:'invoices',   label:'Invoices'    },
    { key:'reports',    label:'Reports'     },
    { key:'carriers',   label:'Carriers'    },
    { key:'bundles',    label:'Bundles'     },
    { key:'dids',       label:'DID Billing' },
    { key:'reseller',   label:'Reseller'    },
    { key:'ra',         label:'Rev. Assurance' },
    { key:'tax',        label:'Tax'         },
  ] as { key: TabKey; label: string }[]).filter((t) => {
    // 2026-10-28: a group limited to certain billing pages (e.g. Operations) only sees those tabs
    try {
      const bp = JSON.parse(localStorage.getItem('ce_session') || '{}')?.user?.access_profile?.restrictions?.billing_pages;
      if (!Array.isArray(bp) || !bp.length) return true;
      return Object.entries(tabMap).some(([page, key]) => key === t.key && bp.includes(page));
    } catch (_) { return true; }
  });
  return (
    <div style={{ padding: '24px 28px' }}>
      <div style={{ display:'flex',gap:2,marginBottom:24,borderBottom:'1px solid #334155',overflowX:'auto' }}>
        {tabs.map(t=>(
          <button key={t.key} onClick={()=>setTab(t.key)}
            style={{ background:'none',border:'none',color: tab===t.key?'#6366f1':'#64748b',fontWeight: tab===t.key?700:500,fontSize:13,padding:'10px 14px',cursor:'pointer',borderBottom: tab===t.key?'2px solid #6366f1':'2px solid transparent',marginBottom:'-1px',whiteSpace:'nowrap' }}>
            {t.label}
          </button>
        ))}
      </div>
      {tab==='dashboard'  && <BillingDashboard />}
      {tab==='accounts'   && <BillingAccounts user={user} />}
      {tab==='rate-plans' && <RatePlansPage />}
      {tab==='invoices'   && <InvoicesPage />}
      {tab==='reports'    && <BillingReports siteId={siteId} />}
      {tab==='carriers'   && <CarriersPage />}
      {tab==='bundles'    && <BundlesPage />}
      {tab==='dids'       && <DidBillingPage />}
      {tab==='reseller'   && <ResellerPage />}
      {tab==='ra'         && <RevenueAssurancePage />}
      {tab==='tax'        && <TaxSettingsPage />}
    </div>
  );
}
